export const formatHelper = {
    isNumeric(str) {
        return /^\d+$/.test(str);
    },
    splitString(inputString, numParts) {
        if (inputString.length % numParts !== 0) {
            console.error("The length of the input string is not divisible by the number of parts.");
            return [];
        }

        const partLength = inputString.length / numParts;
        const parts = [];

        for (let i = 0; i < numParts; i++) {
            const startIndex = i * partLength;
            const endIndex = startIndex + partLength;
            const part = inputString.substring(startIndex, endIndex);
            parts.push(part);
        }

        return parts;
    },
    formatNumber(number, locale = 'en-US', symbol = '') {
        if(typeof number !== 'number') {
            return '';
        }

        if(isNaN(number)) {
            return '';
        }

        return ( symbol ? (symbol + ' ') : '') + (new Intl.NumberFormat(locale, {
            style: 'decimal',
            minimumFractionDigits: (number % 1 === 0 ? 0 : 2),
            maximumFractionDigits: 2
        })).format(number);
    },
    numberWithoutTrailingZero(number) {
        // Check if the input is a valid number
        if (number === null || number === undefined || ((typeof number) !== 'number' && isNaN(parseFloat(number)) )) {
            return null;
        }

        if((typeof number) !== 'number') {
            number = parseFloat(number);
        }

        // Convert the number to a string
        let numStr = number.toString().replace(",", ".");

        // Split the number into integer and decimal parts (if any)
        const parts = numStr.split(".");

        // If there is a decimal part and it's all zeros, return only the integer part
        if (parts.length > 1 && /^0+$/.test(parts[1])) {
            return parts[0]; // Return the integer part only
        }

        // Otherwise, return the full number as it is (integer and decimal)
        return numStr;
    },
    parseBoolean(value) {
        if (typeof value === 'string') {
            value = value.toLowerCase().trim();
            return value === 'true' || value === '1';
        }
        return Boolean(value);
    },
    resizeImage(input, maxWidth, maxHeight) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            // Handle input types: File, URL, or base64 data:image
            if (input instanceof File) {
                const reader = new FileReader();
                reader.onload = function(event) {
                    img.src = event.target.result;
                };
                reader.readAsDataURL(input);
            } else if (typeof input === 'string' && input.startsWith('data:image/')) {
                img.src = input;
            } else if (typeof input === 'string') {
                img.src = input;
            } else {
                reject(new Error('Invalid input type. Expected a File, URL, or base64 string.'));
                return;
            }

            // Once the image is loaded, resize it
            img.onload = function() {
                // Get original image dimensions
                let originalWidth = img.width;
                let originalHeight = img.height;

                // Calculate the new width and height while maintaining the aspect ratio
                let width = originalWidth;
                let height = originalHeight;

                if (originalWidth > maxWidth || originalHeight > maxHeight) {
                    const widthRatio = maxWidth / originalWidth;
                    const heightRatio = maxHeight / originalHeight;

                    // Use the smaller ratio to ensure the image fits within the max dimensions
                    const ratio = Math.min(widthRatio, heightRatio);

                    width = originalWidth * ratio;
                    height = originalHeight * ratio;
                }

                // Create a canvas to draw the resized image
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set canvas size to the new dimensions
                canvas.width = width;
                canvas.height = height;

                // Draw the resized image on the canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Convert the canvas to a Blob or base64 string and resolve the promise
                canvas.toBlob(function(blob) {
                    if (blob) {
                        const resizedImageUrl = URL.createObjectURL(blob);
                        resolve(resizedImageUrl);  // Resolve with the resized image URL
                    } else {
                        reject(new Error('Failed to create Blob from canvas.'));
                    }
                }, 'image/jpeg', 0.95);  // Adjust format and quality as needed
            };

            img.onerror = function() {
                reject(new Error('Failed to load the image. Please check the input.'));
            };
        });
    },
    formatFileSize(bytes) {
        if (typeof bytes !== 'number' || isNaN(bytes)) return '';
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDecimal(number, toInteger = false) {
        if (isNaN(number)) {
            return null;
        }

        if (toInteger) {
            return Math.ceil(number);
        }

        // Round up to 2 decimal places
        const roundedNumber = Math.ceil(number * 100) / 100;

        // Format the number to always show 2 decimal places
        const formattedNumber = roundedNumber.toFixed(2);

        // If the formatted number ends with '.00', return it as an integer
        if (formattedNumber.endsWith('.00')) {
            return parseInt(formattedNumber);
        }

        return parseFloat(formattedNumber);
    }
}
